import { Head, Link, usePage } from '@inertiajs/react'

import { GitHubIcon } from '@/components/github-icon'
import { Logo } from '@/components/logo'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'

type Props = {
  githubConfigured: boolean
}

function csrfToken() {
  if (typeof document === 'undefined') return ''
  return document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content ?? ''
}

export default function SessionsNew({ githubConfigured }: Props) {
  const { flash } = usePage().props

  return (
    <>
      <Head title="Sign in">
        <meta
          head-key="description"
          name="description"
          content="Sign in to Git Digest with GitHub to send team commit digests to Slack and email. Replace daily standups with async codebase reports."
        />
      </Head>
      <div className="bg-muted flex min-h-[100dvh] flex-col">
        <div className="flex flex-1 flex-col items-center justify-center px-4 py-12 sm:px-6 lg:px-8">
          <div className="w-full max-w-md">
            <div className="mb-8 flex justify-center">
              <Link href="/">
                <Logo className="text-3xl" />
              </Link>
            </div>
            <Card className="bg-background rounded-lg shadow-md">
              <CardHeader className="pb-4">
                <h1 className="text-foreground text-center text-2xl font-bold">Sign in to Git Digest</h1>
                <CardTitle className="text-muted-foreground mt-2 text-center text-base font-medium">
                  Continue with GitHub
                </CardTitle>
                <p className="text-muted-foreground mt-1 text-center text-sm">
                  Git Digest uses GitHub to sign you in and connect your repositories for team commit digests.
                </p>
              </CardHeader>
              <CardContent className="space-y-6">
                {(flash?.alert || flash?.notice) && (
                  <p className={`text-center text-sm ${flash.alert ? 'text-destructive' : 'text-golden-700'}`}>
                    {flash.alert || flash.notice}
                  </p>
                )}

                {githubConfigured ? (
                  <form action="/auth/github" method="post">
                    <input type="hidden" name="authenticity_token" value={csrfToken()} />
                    <Button type="submit" variant="default" className="flex w-full items-center justify-center space-x-2 border">
                      <GitHubIcon className="h-5 w-5" />
                      <span>Continue with GitHub</span>
                    </Button>
                  </form>
                ) : (
                  <p className="border-border bg-muted text-muted-foreground rounded-lg border px-4 py-3 text-center text-sm">
                    Set <code>GITHUB_CLIENT_ID</code> and <code>GITHUB_CLIENT_SECRET</code> in <code>.env</code> to
                    enable GitHub OAuth.
                  </p>
                )}

              </CardContent>
            </Card>
            <div className="text-muted-foreground mt-6 flex justify-center space-x-2 text-xs">
              <Link href="/privacy" className="hover:text-[#E6A91A] transition-colors hover:underline">
                Privacy Policy
              </Link>
              <span aria-hidden="true">•</span>
              <Link href="/terms" className="hover:text-[#E6A91A] transition-colors hover:underline">
                Terms of Service
              </Link>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
